import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, Button, Chip, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Modal from 'components/Modal';
import useConfirm from 'core/hooks/useConfirm';
import EditIcon from '@mui/icons-material/Edit';
import EditUser from './edit';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter'
import '@inovua/reactdatagrid-community/index.css'
import { getVendorListWithActivityStatus, activateVendor, deactivateVendor } from 'services/vendors';
import DownloadIcon from '@mui/icons-material/Download';
import { reactDataGridExportCSV } from 'components/reactdata-grid';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from 'core/context';
import { isLinkAccessable } from 'core/utils/common';

const VendorsPage = () => {

  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }));
  const [gridRef, setGridRef] = useState(null);
  const [vendorsList, setVendorList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [vendorInfo, setVendorInfo] = useState({ info: null });
  const authContext = useContext(AuthContext);
  const { permissions = [] } = authContext?.data;

  const onActionBtnClick = useCallback((e, type, userData) => {
    e.preventDefault();
    if(type === 'edit'){
      editVendorDetails(userData);
    }
  }, []);

  const defaultFilterValue = [
    { name: 'firstName', operator: 'contains', type: 'string', value: '' },
    { name: 'email', operator: 'contains', type: 'string', value: '' },
    { name: 'mobile', operator: 'contains', type: 'string', value: '' },
    { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
    { name: 'active', operator: 'eq', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
      if(filterValue === null){
        return true;
      };
      const status = filterValue === 'active' ? true : false;
      return data?.active === status;

    } },
  ];

  const columns = useMemo(() => ([
    { name: 'firstName', header: 'Name', width: 260},
    { name: 'email', header: 'Email', width: 280},
    { name: 'mobile', header: 'Mobile', width: 150},
    { name: 'propertyCount', header: 'Properties', width: 120},
    { name: 'active', header: 'Status', width: 150, textAlign: 'center', render: ({data}) => {
        return <Chip label={data?.active ? 'Active' : 'Inactive'} color={data?.active ? 'success' : 'default'} size="small" />
      },
      filterEditor: SelectFilter,
      filterEditorProps: {
        placeholder: 'All',
        dataSource: [{id: 'active', label: 'Active'}, {id: 'inactive', label: 'Inactive'}]
      },
    },
    { name: 'createdOn', header: 'Created On', width: 150},
    { name: 'actions', header: 'Actions', width: 220, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Edit Student Details" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'edit', data)}>
                <EditIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title={data?.active ? 'Deactivate Vendor' : 'Activate Vendor'} arrow placement="left">
              <Button
                onClick={() => changeUserState(data?.userId, data?.active)} 
                size="small" 
                variant="contained"
                sx={{textTransform: 'none'}}
                color={data?.active ? 'error' : 'success'}
                >
                  {data?.active ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
          </Box>
        </Stack>
      )
    }},
  ]),[onActionBtnClick]);

  useEffect(() => {
    getVendorsData('ALL');
  }, []);

  function getVendorsData(state) {
    getVendorListWithActivityStatus({state}).then(({response, error}) => {
      if(response){
        setVendorList([...(response || [])]);
      } else {
        setVendorList([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const editVendorDetails = (userData) => {
    setVendorInfo({ info: userData });
    setOpenModal(true);
  }

  const changeUserState = (userId, isActive) => {
    confirm({ title: `${isActive ? 'Deactivate Vendor' : 'Activate Vendor'}`, description: 'Are you sure you want to proceed?' })
    .then(() => { 
      if(isActive){
        _deactivateVendor(userId);
      }else{
        _activateVendor(userId);
      }
    }).catch(() => {});
  }

  function _activateVendor(userId) {
    activateVendor({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Vendor activated successfully", {variant: 'success?'});
        getVendorsData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function _deactivateVendor(userId) {
    deactivateVendor({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Vendor deactivated successfully", {variant: 'success?'});
        getVendorsData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )

 
  const onSuccess = useCallback(
    () => {
      setOpenModal(false);
      getVendorsData();
      setVendorInfo({id: null});
    },
    [setOpenModal],
  )

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}} >
          Vendors
        </Typography>
        {isLinkAccessable(['View vendor dashboard'], permissions) ? <Button  variant='contained' color="info" component={RouterLink} to='/dashboard/vendor/' size="small" sx={{textTransform: 'none'}}>Dashboard</Button> : null}
      </Stack>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12} lg={12}  >
            <Box justifyContent="space-between" display="flex">
              <Box minWidth={160}>
                <FormControl fullWidth>
                  <InputLabel id="state-select-label">Activity Status</InputLabel>
                  <Select
                    size="small"
                    labelId="state-select-label"
                    defaultValue="ALL"
                    label="Activity Status"
                    onChange={(evt) => getVendorsData(evt.target.value)}
                    >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="HYPER_ACTIVE">Hyper Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                    <MenuItem value="LESS_ACTIVE">Less Active</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button onClick={() => reactDataGridExportCSV(gridRef, 'vendors.csv') } startIcon={<DownloadIcon />} variant="outlined" color="success" sx={{textTransform: 'none'}}>Export CSV</Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{  maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={vendorsList}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
      <Modal  maxWidth={"sm"} open={openModal} onModalClose={onModalClose} title={'Edit Vendor'}>
        <EditUser vendorData={vendorInfo.info} onSuccess={onSuccess} onFailure={onModalClose} />
      </Modal>
    </Paper>
  );
}
 
export default React.memo(VendorsPage);
